import React, { useState } from "react";
import { WrittenFeedbackForm } from "./FeedbackComponents.tsx";
import { callWithAuth, postNPS, postNPSFeedback } from "@/api/Api.ts";
import { useMutation } from "@tanstack/react-query";
import { CloseButton } from "@chakra-ui/react";
import { requestAppRating, isAppRatingAvailable } from "@/lib/app_rating";

interface FeedbackPopupProps {
  isOpen: boolean;
  onClose: () => void;
}

const FeedbackPopup: React.FC<FeedbackPopupProps> = ({ isOpen, onClose }) => {
  const [stage, setStage] = useState<
    "nps" | "negative" | "complete"
  >("nps");
  const [score, setScore] = useState<number | null>(null);
  // const [isIOS, setIsIOS] = useState(false);

  // Check if user is on iOS device
  //useEffect(() => {
  //const userAgent = navigator.userAgent;
  //const isIOSDevice =
  // /iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream;
  //setIsIOS(isIOSDevice);
  //}, []);

  const npsMutation = useMutation({
    mutationFn: ({
      rating,
      isSkipScore,
    }: {
      rating: number;
      isSkipScore: boolean;
    }) => callWithAuth(postNPS, { rating, isSkipScore }),
    onSuccess: () => {
      console.log("NPS rating submitted successfully");
    },
    onError: (error) => {
      console.error("Failed to submit NPS rating:", error);
    },
  });

  const npsFeedbackMutation = useMutation({
    mutationFn: (feedback: string) =>
      callWithAuth(postNPSFeedback, { feedback }),
    onSuccess: () => {
      setStage("complete");
    },
    onError: (error) => {
      console.error("Failed to submit NPS feedback:", error);
    },
  });

  const handleClickClose = () => {
    if (stage === "nps") {
      const isSkipScore = true;
      npsMutation.mutate({ rating: 0, isSkipScore });
    }
    onClose();
  };

  const handleScoreSelection = (selectedScore: number) => {
    setScore(selectedScore);
    npsMutation.mutate({ rating: selectedScore, isSkipScore: false });

    // Add a 100ms delay before changing the stage
    setTimeout(() => {
      if (selectedScore >= 7) {
        // High score (7+) -> Check if App Store rating is available
        if (isAppRatingAvailable()) {
          (async () => {
            try {
              await requestAppRating();
              setStage("complete");
              setTimeout(() => {
                onClose();
                setStage("nps");
                setScore(null);
              }, 2000);
            } catch (error) {
              setStage("negative");
            }
          })();
        } else {
          // App rating not available, fall back to feedback form
          setStage("negative");
        }
      } else {
        // Low score (6 or below) -> Feedback form
        setStage("negative");
      }
    }, 100);
  };

  const submitFeedback = (feedback: string) => {
    // Mock API call
    npsFeedbackMutation.mutate(feedback);
    setStage("complete");
    // Close the popup after a short delay
    setTimeout(() => {
      onClose();
      // Reset state for next time
      setStage("nps");
      setScore(null);
    }, 5000);
  };

  if (!isOpen) {
    return null;
  }
  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-50 px-4 md:px-0"
      style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
    >
      <div className="bg-white rounded-lg p-4 pt-3 w-full max-w-sm mx-auto">
        <div className="flex justify-between items-start mb-5">
          <h2 className="text-xl font-semibold pt-1">Feedback</h2>
          <CloseButton onClick={handleClickClose} w={4} h={4} />
        </div>

        {stage === "nps" && (
          <div>
            <p className="mb-5 text-left font-medium">
              How likely are you to recommend Parrot to a friend?
            </p>
            <div className="grid grid-cols-10 gap-1 mb-5">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                <button
                  key={num}
                  onClick={() => handleScoreSelection(num)}
                  className={`w-7 h-7 rounded-full flex items-center justify-center ${
                    score === num
                      ? "bg-[var(--color-blue)] text-white border-[var(--color-blue)]"
                      : "border border-gray-300 hover:border-[var(--color-blue)] hover:bg-[var(--color-blue)]"
                  }`}
                >
                  {num}
                </button>
              ))}
            </div>
            <div className="flex justify-between text-sm text-[var(--color-grey)] px-1">
              <span>Not likely</span>
              <span>Very likely</span>
            </div>
          </div>
        )}

        {stage === "negative" && (
          <WrittenFeedbackForm
            onSubmit={submitFeedback}
            title="What's the most important reason for your score?"
          />
        )}

        {stage === "complete" && (
          <div className="text-center py-6">
            <p className="text-lg">Thank you for your feedback!</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default FeedbackPopup;
